import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { apiWrite } from '../lib/api'
import { useCase } from '../lib/queries'
import { PageHead, Skel, Empty } from '../components/ui'
import { money, thaiDate, thaiDateTime } from '../lib/format'
import { StatusChip, caseStatus } from './Cases'

const actionLabel: Record<string, string> = {
  investigating: 'เริ่มสอบสวน',
  pending_review: 'ส่งทวนสอบ',
  approved: 'อนุมัติเคส',
  rejected: 'ตีกลับ',
  closed: 'ปิดเคส',
}

const actions: Record<string, string[]> = {
  open: ['investigating', 'closed'],
  investigating: ['pending_review', 'closed'],
  pending_review: ['approved', 'rejected'],
  approved: ['closed'],
  rejected: ['investigating', 'closed'],
  closed: [],
}

export function CaseDetail() {
  const { id } = useParams()
  const q = useCase(id)
  const qc = useQueryClient()
  const move = useMutation({
    mutationFn: (status: string) => apiWrite(`/v1/cases/${id}`, 'PATCH', { status }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['cases'] })
      void qc.invalidateQueries({ queryKey: ['case', id] })
    },
  })

  if (q.isLoading) return <Skel h={320} />
  if (!q.data) return <Empty title="ไม่พบเคส" body="อาจถูกลบหรือคุณไม่มีสิทธิ์เข้าถึง" />
  const c = q.data

  return (
    <>
      <PageHead title={c.title} sub={`${c.case_no} · เปิด ${thaiDate(c.opened_at)}`}
        actions={<div className="row" style={{ gap: 8 }}>
          <Link className="btn btn-ghost" to="/cases">กลับ</Link>
          {(actions[c.status] ?? []).map((next) => (
            <button key={next} className={`btn ${next === 'approved' ? 'btn-primary' : 'btn-ghost'}`}
              disabled={move.isPending} onClick={() => move.mutate(next)}>
              {actionLabel[next] ?? caseStatus[next] ?? next}
            </button>
          ))}
        </div>} />

      {move.isError ? <div className="form-error" style={{ marginBottom: 14 }}>เปลี่ยนสถานะไม่ได้ในขั้นนี้</div> : null}

      <div className="grid-main">
        <div className="card card-pad">
          <div className="row">
            <StatusChip status={c.status} />
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
              {c.finding_count} ข้อสังเกต · ความเสี่ยงรวม {money(c.amount_at_risk_satang)}
            </span>
          </div>

          <div className="card-label" style={{ marginTop: 24 }}>ข้อสังเกตในเคส</div>
          <div className="stack" style={{ marginTop: 10 }}>
            {c.findings.map((f) => (
              <Link key={f.id} to={`/findings/${f.id}`} className="row" style={{ padding: '10px 0', borderBottom: '1px solid var(--color-line)', color: 'inherit', textDecoration: 'none' }}>
                <div>
                  <div style={{ fontSize: 13 }}>{f.title}</div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{f.rule_code} · ความเสี่ยง {f.risk_score}</div>
                </div>
                <div className="num" style={{ marginLeft: 'auto' }}>{money(f.amount_at_risk_satang)}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-label">ไทม์ไลน์</div>
          <ol style={{ listStyle: 'none', margin: '14px 0 0', padding: 0 }}>
            {c.events.map((e, i) => (
              <li key={e.id} style={{ display: 'flex', gap: 12, paddingBottom: 14 }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, marginTop: 6, flex: '0 0 auto',
                  background: i === c.events.length - 1 ? 'var(--color-gold)' : 'var(--color-line-strong)' }} />
                <div>
                  <div style={{ fontSize: 12.5 }}>
                    {e.event_type === 'created' ? 'เปิดเคส'
                      : e.event_type === 'finding_added' ? 'เพิ่มข้อสังเกต'
                      : `${caseStatus[e.from_status ?? ''] ?? e.from_status} → ${caseStatus[e.to_status ?? ''] ?? e.to_status}`}
                  </div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>{thaiDateTime(e.created_at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </>
  )
}
